import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { BrandModel, BrandTitleModel, HxBrandService, HxUiLabelPipe, PagedList, SaveBrandRequest } from 'hx-services';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NzModalService } from 'ng-zorro-antd/modal';
import { BrandTitleEditModal } from '@adm-web-app/modules/brands/modal/titles-edit/title-edit.modal';
import { HxPagedListDirective } from 'hx-components';
import { firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderExtraDirective, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { DatePipe, JsonPipe, NgFor, NgIf } from '@angular/common';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent, NzButtonGroupComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzTableCellDirective, NzTableComponent, NzTbodyComponent, NzThAddOnComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';

@Component({
    selector: 'hc-brand-edit',
    templateUrl: './brand-edit.component.html',
    styleUrls: ['./brand-edit.component.less'],
  imports: [NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, TranslocoDirective, NzSkeletonComponent, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, NzInputDirective, NgIf, NzAlertComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, NzTagComponent, NzDividerComponent, NzPageHeaderExtraDirective, NzTableComponent, NzTheadComponent, NzTrDirective, NzTableCellDirective, NzThMeasureDirective, NzThAddOnComponent, NzTbodyComponent, NgFor, NzButtonGroupComponent, NzPopconfirmDirective, JsonPipe, DatePipe, HxUiLabelPipe, TranslocoPipe]
})
export class BrandEditComponent extends HxPagedListDirective<BrandTitleModel> implements OnInit {
  model!: BrandModel;
  isEdit = false;
  isCreate = false;
  form!: UntypedFormGroup;
  isLoading = {
    model: false,
  };
  isSaving = false;

  constructor(
    override aRoute: ActivatedRoute,
    override router: Router,
    protected fb: UntypedFormBuilder,
    protected brandService: HxBrandService,
    protected modal: NzModalService,
    protected notification: NzNotificationService,
    protected tr: TranslocoService,
  ) {
    super(router, aRoute);
    this.limit = 20;
  }

  ngOnInit(): void {
    const routeData: Data = this.aRoute.snapshot.data;
    this.model = routeData['model'] as BrandModel;
    if (this.model) {
      this.isEdit = true;
    } else {
      this.model = {} as BrandModel;
      this.isCreate = true;
    }
    this.form = this.fb.group({
      title: this.fb.control(null, Validators.required),
    });
    const formModel: Partial<{title: string}> = {
      title: this.model.title,
    };
    this.form.patchValue(formModel);
    this.aRoute.queryParamMap.pipe(takeUntil(this.$destroyed))
      .subscribe(paramMap => {
        const asNumberFn = (name: string, def?: number) => paramMap.get(name) ? Number(paramMap.get(name)) : def;
        // const asBooleanFn = (name: string, def?: boolean) => paramMap.get(name) ? paramMap.get(name) === 'true' : def;
        super.load(paramMap);
      });
  }

  async submitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      const formModel = this.form.getRawValue() as BrandModel;
      const model: SaveBrandRequest = {
        id: this.model.id,
        title: formModel.title,
      };

      this.isSaving = true;
      try {
        const result = await this.brandService.saveBrand(model);
        this.form.markAsPristine();
        if (this.isCreate) {
          this.router.navigate([`/brands/${result.id}/edit`]);
        } else {
          this.isLoading.model = true;
          this.model = await this.brandService.getBrandById(result.id);
          this.isLoading.model = false;
        }
      } catch (err: any) {
        this.notification.error(this.tr.translate('nav.Brands'), this.tr.translate(err?.error?.message));
      } finally {
        this.isSaving = false;
      }
    } else {
      console.log('not valid form', this.form.errors);
    }
  }

  addBrandTitle() {
    const modalRef = this.modal.create({
      nzTitle: this.tr.translate('breadcrumb.Brands'),
      nzContent: BrandTitleEditModal,
      nzFooter: null
    });
    if (modalRef.componentInstance) {
      modalRef.componentInstance.brandId = this.model.id;
    }
    modalRef.afterClose.subscribe(() => this.loadData());
  }

  editBrandTitle(brandTitleId: number) {
    const modalRef = this.modal.create({
      nzTitle: this.tr.translate('breadcrumb.Brands'),
      nzContent: BrandTitleEditModal,
      nzFooter: null
    });
    if (modalRef.componentInstance) {
      modalRef.componentInstance.brandId = this.model.id;
      modalRef.componentInstance.brandTitleId = brandTitleId;
    }
    modalRef.afterClose.subscribe(() => this.loadData());
  }

  async remove(id: number) {
    await this.brandService.removeBrandTitle(id);
    this.loadData();
    this.notification.success(this.tr.translate('nav.Brands'), this.tr.translate('nt.dataRemoved'));
  }

  protected loadList(): Promise<PagedList<BrandTitleModel>> {
    return this.brandService.getBrandTitles({
      brandId: this.model.id,
      limit: this.limit,
      page: this.page,
    }) as unknown as Promise<PagedList<BrandTitleModel>>;
  }
}
